import { useState } from "react";

import { sendNameSet } from "../net/colyseus";
import { DraggableOverlay } from "../util/DraggableOverlay";
import { useIsTouch } from "../util/useIsTouch";
import { useStore } from "../store";

/**
 * Custom-name editor — a holder-only perk. Token holders (server-verified
 * `isHolder`) get an input to set their display name; everyone else sees a
 * locked hint. The server re-checks holder status and ignores `name:set` from
 * non-holders, so this is UX, not the security boundary.
 *
 * Pointer-lock blocks DOM interaction (cursor trapped in the canvas), so the
 * toggle exits lock first — same pattern as SkinPicker.
 */
function releasePointerLock(): void {
  if (typeof document === "undefined") return;
  if (document.pointerLockElement) document.exitPointerLock?.();
}

export function NameEditor(): JSX.Element | null {
  const isTouch = useIsTouch();
  const localPlayer = useStore((s) => s.localPlayer);
  const isSpectator = localPlayer?.isSpectator === true;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  if (!localPlayer || isSpectator) return null;

  const isHolder = localPlayer.isHolder;

  const onToggle = (): void => {
    releasePointerLock();
    setOpen((o) => {
      const next = !o;
      if (next) setDraft(localPlayer.displayName ?? "");
      return next;
    });
  };

  const onSave = (): void => {
    const name = draft.trim();
    if (name.length > 0) sendNameSet(name);
    setOpen(false);
  };

  return (
    <DraggableOverlay
      id="name-editor"
      defaultAnchor={{ right: 12, top: 176, safeAreaTop: isTouch }}
      enabled={isTouch}
      zIndex={25}
      nonDragSelector="button, input"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          alignItems: "flex-end",
          userSelect: "none",
          pointerEvents: "auto",
        }}
      >
        <button
          onClick={onToggle}
          title={isHolder ? "Set a custom name" : "Hold the token to unlock custom names"}
          aria-label="Edit display name"
          style={{
            height: 30,
            padding: "0 10px",
            borderRadius: 15,
            border: "1px solid #2a2a36",
            background: isHolder ? "rgba(244,211,106,0.16)" : "rgba(15,16,24,0.9)",
            color: isHolder ? "#f4d36a" : "#9aa0b4",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {isHolder ? "✦ name" : "🔒 name"}
        </button>

        {open && (
          <div
            style={{
              padding: 10,
              background: "rgba(15,16,24,0.96)",
              border: "1px solid #2a2a36",
              borderRadius: 8,
              width: 200,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {isHolder ? (
              <>
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSave();
                  }}
                  placeholder="your name"
                  maxLength={24}
                  autoFocus
                  style={{
                    background: "#0e0f17",
                    border: "1px solid #2a2a36",
                    borderRadius: 4,
                    padding: "5px 8px",
                    color: "#e8e8ef",
                    fontSize: 13,
                    outline: "none",
                  }}
                />
                <button
                  onClick={onSave}
                  style={{
                    background: "#f4d36a",
                    color: "#1a1505",
                    border: "none",
                    borderRadius: 4,
                    padding: "5px 10px",
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Save name
                </button>
              </>
            ) : (
              <div style={{ fontSize: 12, color: "#cdd3e0", lineHeight: 1.4 }}>
                Custom names are a holder perk. Buy &amp; hold the token to set
                your own name and get the ✦ badge.
              </div>
            )}
          </div>
        )}
      </div>
    </DraggableOverlay>
  );
}
